import * as React from "react";
import { Search, Filter, RefreshCw } from "lucide-react";
import { Button } from "./button";
import { Input } from "./input";
import { Label } from "./label";
import { Switch } from "./switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./select";
import { DatePicker } from "./date-picker";

/**
 * ListViewHeader - Standardized header component for list views
 * 
 * Standard Layout:
 * ┌─────────────────────────────────────────────────────────────────────────┐
 * │ [Count + Refresh]    [Search...]    [Status ▼]    [Date Filter Buttons] │
 * ├─────────────────────────────────────────────────────────────────────────┤
 * │ [○ Custom date range]   [From: ____]   [To: ____]   (optional row)      │
 * └─────────────────────────────────────────────────────────────────────────┘
 * 
 * Usage:
 * <ListViewHeader
 *   count={filteredItems.length}
 *   countLabel="Jobs"
 *   onRefresh={handleRefresh}
 *   refreshing={isRefreshing}
 *   searchTerm={searchTerm}
 *   onSearchChange={setSearchTerm}
 *   searchPlaceholder="Search jobs..."
 *   statusFilter={statusFilter}
 *   onStatusFilterChange={setStatusFilter}
 *   statusOptions={[
 *     { value: 'all', label: 'All Status' },
 *     { value: 'active', label: 'Active' },
 *   ]}
 *   dateFilter={dateFilter}
 *   onDateFilterChange={setDateFilter}
 *   dateFilterOptions={[
 *     { value: 'today', label: 'Today' },
 *     { value: 'all', label: 'All' },
 *   ]}
 *   showCustomDateRange={true}
 *   customDateEnabled={dateFilterEnabled}
 *   onCustomDateToggle={setDateFilterEnabled}
 *   dateFrom={dateFrom}
 *   onDateFromChange={setDateFrom}
 *   dateTo={dateTo}
 *   onDateToChange={setDateTo}
 * />
 */

const ListViewHeader = ({
  // Count & Refresh
  count,
  countLabel = "items",
  onRefresh,
  refreshing = false,
  showCount = true,
  showRefresh = true,
  
  // Search
  searchTerm,
  onSearchChange,
  searchPlaceholder = "Search...",
  searchTestId = "list-search",
  
  // Status Filter
  statusFilter,
  onStatusFilterChange,
  statusOptions = [],
  statusPlaceholder = "All Status",
  statusTestId = "status-filter",
  showStatusFilter = true,
  
  // Date Filter Buttons
  dateFilter,
  onDateFilterChange,
  dateFilterOptions = [],
  showDateFilter = true,
  
  // Custom Date Range
  showCustomDateRange = false,
  customDateEnabled = false,
  onCustomDateToggle,
  dateFrom,
  onDateFromChange,
  dateTo,
  onDateToChange,
  formatDateDisplay,
  
  // Additional content
  children,
}) => {
  return (
    <div className="flex flex-col gap-4">
      {/* Main Filter Row */}
      <div className="flex flex-col lg:flex-row lg:items-center gap-4">
        {/* Left Section: Count + Refresh */}
        {(showCount || showRefresh) && (
          <div className="flex items-center gap-2 shrink-0">
            {showCount && (
              <span className="font-heading text-lg font-semibold">
                {count} {countLabel}
              </span>
            )}
            {showRefresh && onRefresh && (
              <Button
                variant="ghost"
                size="icon"
                onClick={onRefresh}
                disabled={refreshing}
                className="h-8 w-8"
                data-testid="refresh-list-btn"
              >
                <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
              </Button>
            )}
          </div>
        )}
        
        {/* Center Section: Search */}
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            data-testid={searchTestId}
            placeholder={searchPlaceholder}
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            className="pl-9"
          />
        </div>
        
        {/* Right Section: Status Filter + Date Buttons */}
        <div className="flex flex-wrap items-center gap-2">
          {/* Status Filter */}
          {showStatusFilter && statusOptions.length > 0 && (
            <Select value={statusFilter} onValueChange={onStatusFilterChange}>
              <SelectTrigger className="w-[160px]" data-testid={statusTestId}>
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder={statusPlaceholder} />
              </SelectTrigger>
              <SelectContent>
                {statusOptions.map(({ value, label }) => (
                  <SelectItem key={value} value={value}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          
          {/* Date Filter Buttons */}
          {showDateFilter && dateFilterOptions.length > 0 && (
            <div className="flex flex-wrap items-center gap-1">
              {dateFilterOptions.map(({ value, label }) => (
                <Button
                  key={value}
                  variant={dateFilter === value ? 'default' : 'outline'}
                  size="sm"
                  className={dateFilter === value ? 'bg-[#ffd108] text-black hover:bg-[#ffd108]/90' : ''}
                  onClick={() => onDateFilterChange(value)}
                  data-testid={`date-filter-${value}`}
                >
                  {label}
                </Button>
              ))}
            </div>
          )}
        </div>
      </div>
      
      {/* Custom Date Range Row (Optional) */}
      {showCustomDateRange && (
        <div className="flex flex-wrap items-center gap-3 pt-3 border-t border-border">
          <div className="flex items-center gap-2">
            <Switch
              checked={customDateEnabled}
              onCheckedChange={onCustomDateToggle}
              data-testid="custom-date-toggle"
            />
            <Label className="text-sm text-muted-foreground">Custom date range</Label>
          </div>
          {customDateEnabled && (
            <>
              <div className="flex items-center gap-2">
                <Label className="label-overline text-xs">From</Label>
                <DatePicker
                  value={dateFrom}
                  onChange={onDateFromChange}
                  formatDisplay={formatDateDisplay}
                  className="w-[160px] h-8 text-sm"
                  testId="date-from-input"
                />
              </div>
              <div className="flex items-center gap-2">
                <Label className="label-overline text-xs">To</Label>
                <DatePicker
                  value={dateTo}
                  onChange={onDateToChange}
                  formatDisplay={formatDateDisplay}
                  minDate={dateFrom}
                  className="w-[160px] h-8 text-sm"
                  testId="date-to-input"
                />
              </div>
            </>
          )}
        </div>
      )}
      
      {/* Additional Content */}
      {children}
    </div>
  );
};

ListViewHeader.displayName = "ListViewHeader";

export { ListViewHeader };
